import {
  Component,
  HostBinding,
  NgZone,
  ViewContainerRef,
  viewChild,
  input,
  output,
  signal,
  effect,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ValidationErrors } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule } from '@ngx-translate/core';
import { GalaxyAiIconService } from '@vendasta/galaxy/ai-icon';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { GalaxyTooltipModule } from '@vendasta/galaxy/tooltip';
import { ProductAnalyticsService } from '@vendasta/product-analytics';
import { firstValueFrom } from 'rxjs';
import { DynamicComponentSelectorModule } from '../../components/dynamic-component-selector';
import { RichTextEditorComponent } from '../../components/rich-text-editor/rich-text-editor.component';
import { SuggestContentComponent } from '../../components/suggest-content-dialog/suggest-content-dialog.component';
import { EmailPreviewRefreshService } from '../../services/email-preview-refresh.service';
import { AfterBlockDragService } from './after-block-drag.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

// Interface
export interface RichTextBlock {
  rawHtml?: string;
}

// Default blank value
export const blankRichTextBlock: RichTextBlock = {
  rawHtml: '', // can't use null or the editor breaks
};

@Component({
  selector: 'email-rich-text-block',
  templateUrl: './rich-text-block.component.html',
  styleUrls: ['./rich-text-block.component.scss'],
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatDialogModule,
    TranslateModule,
    GalaxyTooltipModule,
    GalaxyFormFieldModule,
    DynamicComponentSelectorModule,
    RichTextEditorComponent,
  ],
})
export class RichTextBlockComponent {
  @HostBinding('class') class = 'email-rich-text-block';
  private readonly richTextEditor = viewChild.required<RichTextEditorComponent>('richTextEditor');

  content = input.required<RichTextBlock>();
  contentGenerationEnabled = input(false);
  isBlockValid = output<boolean>();
  protected readonly isValid = signal(true);

  constructor(
    private readonly emailPreview: EmailPreviewRefreshService,
    private readonly afterBlockDrag: AfterBlockDragService,
    private readonly zone: NgZone,
    private readonly dialog: MatDialog,
    private readonly posthogService: ProductAnalyticsService,
    private readonly _: GalaxyAiIconService,
    private readonly viewContainerRef: ViewContainerRef,
  ) {
    this.afterBlockDrag.dragHappenedEmitter$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.richTextEditor().reloadEditor());

    effect(() => {
      this.isBlockValid.emit(this.isValid());
    });
  }

  protected updateText(): void {
    this.zone.run(() => this.emailPreview.refresh());
  }

  protected updateStatus(event: ValidationErrors | null): void {
    this.isValid.set(!event);
  }

  protected async openSuggestAIContentDialog(): Promise<void> {
    this.posthogService.trackEvent('user-clicked-rich-text-generate-content', 'suggest-content', 'click', null);
    const dialogRef = this.dialog.open(SuggestContentComponent, {
      viewContainerRef: this.viewContainerRef,
      width: '600px',
      height: 'auto',
    });
    const content = await firstValueFrom(dialogRef.afterClosed());
    if (content) {
      this.richTextEditor().insertAtCursor(content);
    }
  }
}
